import React from "react";
import {
  Flex,
  Icon,
  Table,
  Tbody,
  Text,
  Th,
  Thead,
  Tr,
  Td,
  useColorModeValue,
} from "@chakra-ui/react";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import { IoCheckmarkDoneCircleSharp } from "react-icons/io5";
import Skeleton from "react-loading-skeleton";
import 'react-loading-skeleton/dist/skeleton.css';

const PackageFlows = ({ title, amount, captions, data, isLoading, variant = "packages" }) => {
  const textColor = useColorModeValue("gray.700", "white");

  // Function to return different colors based on package status (only for packages variant)
  const getStatusColor = (status) => {
    switch (status) {
      case "Pending":
        return "yellow.300";
      case "Awaiting Pick Up":
        return "blue.300";
      case "Picked Up":
        return "green.300";
      case "In Transit":
        return "orange.300";
      case "Delivered":
        return "teal.300";
      case "Canceled":
        return "red.300";
      default:
        return "gray.500";
    }
  };

  // Function to format amount based on variant
  const formatAmount = (amount) => {
    return variant === "spending" ? `P${parseFloat(amount).toFixed(2)}` : amount;
  };

  return (
    <Card p='16px' overflowX={{ sm: "scroll", xl: "hidden" }}>
      <CardHeader p='12px 0px 28px 0px'>
        <Flex direction='column'>
          <Text fontSize='lg' color={textColor} fontWeight='bold' pb='.5rem'>
            {isLoading ? <Skeleton width="150px" height="24px" /> : title}
          </Text>
          <Flex align='center'>
            {isLoading ? (
              <Skeleton circle={true} height="16px" width="16px" />
            ) : (
              <Icon
                as={IoCheckmarkDoneCircleSharp}
                color='teal.300'
                w={4}
                h={4}
                pe='3px'
              />
            )}
            <Text fontSize='sm' color='gray.400' fontWeight='normal' ml={isLoading ? "4px" : "0px"}>
              {isLoading ? (
                <Skeleton width="100px" height="20px" />
              ) : (
                <Text fontWeight='bold' as='span'>
                  {variant === "spending" 
                    ? `Total: ${formatAmount(amount)} spent` 
                    : `${amount} packages processed`}
                </Text>
              )}
            </Text>
          </Flex>
        </Flex>
      </CardHeader>
      <Table variant='simple' color={textColor}>
        <Thead>
          <Tr my='.8rem' ps='0px'>
            {captions?.map((caption, idx) => (
              <Th color='gray.400' key={idx} ps={idx === 0 ? "0px" : null}>
                {isLoading ? <Skeleton width="60px" /> : caption}
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {isLoading ? (
            Array(5)
              .fill()
              .map((_, idx) => (
                <Tr key={idx}>
                  {captions.map((_, i) => (
                    <Td key={i}>
                      <Skeleton height="20px" width="80px" />
                    </Td>
                  ))}
                </Tr>
              ))
          ) : data?.length > 0 ? (
            data.map((row, idx) => (
              <Tr key={idx}>
                {/* Rank Column */}
                <Td fontWeight="bold">
                  {row.rank === 1 ? "🥇" :
                   row.rank === 2 ? "🥈" :
                   row.rank === 3 ? "🥉" :
                   `#${row.rank}`}
                </Td>

                {/* Username Column */}
                <Td>{row.username || row.name || 'N/A'}</Td>

                {/* Dynamic Columns based on variant */}
                {variant === "spending" ? (
                  <>
                    <Td >{formatAmount(row.totalSpend || row.total_spent)}</Td>
                    <Td>{row.lastSpendDate || row.last_spend_date || 'N/A'}</Td>
                  </>
                ) : (
                  <>
                    <Td>{row.packages || row.package_count || 'N/A'}</Td>
                    <Td>{row.lastDate || row.last_date || 'N/A'}</Td>
                    {row.status && (
                      <Td>
                        <Text color={getStatusColor(row.status)} fontWeight="500">
                          {row.status}
                        </Text>
                      </Td>
                    )}
                  </>
                )}
              </Tr>
            ))
          ) : (
            <Tr>
              <Td colSpan={captions.length} textAlign="center">
                <Text color="gray.500">No data to display.</Text>
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>
    </Card>
  );
};

export default PackageFlows;